<template>
  <div class="space-y-4">
    <h2 class="flex items-center gap-2">
      I can has cool example calculationz?
      <img src="@/assets/i-can-haz.png" class="w-10" alt="" />
    </h2>
    <div class="grid grid-cols-3 gap-4">
      <SelectVehicleType :label="t('vehicleType')" @select="vehicleType = $event" />
      <Select v-model="stationId" :label="t('station')" :options="stationOptions" />
      <Input
        v-model="stationLoad"
        :label="t('stationLoad')"
        type="number"
        :placeholder="t('actualStationLoad')"
        adornment="%"
        :min="0"
        :max="100"
      />
      <BookingRangePicker
        v-model:start-date="startDate"
        v-model:end-date="endDate"
        :start-date-title="t('startDate')"
        :end-date-title="t('endDate')"
        class="col-span-2"
      />
    </div>
    <template v-if="calculationResult">
      <Divider />
      <div class="space-y-4">
        <p>
          {{ t('basePricePerDay') }}:
          <span class="font-semibold">{{ formatCurrency(calculationResult.basePrice) }}</span>
        </p>
        <p>{{ t('appliedModifiersPerDay') }}:</p>
        <div class="grid grid-cols-4 items-stretch gap-4">
          <div
            v-for="(day, i) in calculationResult.days"
            :key="i"
            class="rounded-md bg-hazel-100 px-4 py-3"
          >
            <div class="grid grid-cols-[auto_auto] gap-x-4">
              <p class="col-span-2 mb-2 font-semibold">{{ t('day', { count: i + 1 }) }}</p>
              <template v-for="modifier in day.modifiers" :key="modifier.templateId">
                <p class="truncate">{{ getTemplateName(modifier.templateId) }}:</p>
                <p class="font-semibold">×{{ formatFloat(modifier.factor) }}</p>
              </template>
              <p>{{ t('dayPrice') }}:</p>
              <p class="font-semibold">{{ formatCurrency(day.modifiedBasePrice) }}</p>
            </div>
          </div>
        </div>
        <p class="text-right text-lg font-semibold">
          {{ t('totalPrice') }}: {{ formatCurrency(calculationResult.totalModifiedBasePrice) }}
        </p>
      </div>
    </template>
    <Divider />
    <div class="flex justify-end gap-4">
      <CVButton
        variant="default"
        :is-loading="isPending"
        :disabled="!isFilled"
        @click="calculate"
        >{{ t('calculateExample') }}</CVButton
      >
      <CVButton @click="$emit('close')">{{ t('close') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Divider from '@/components/Divider.vue';
import { useI18n } from 'vue-i18n';
import SelectVehicleType from '@/views/yield-management/edit-pricing-modifiers/SelectVehicleType.vue';
import { useStations } from '@/queries/use-stations.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data.ts';
import Select from '@/components/Select.vue';
import Input from '@/components/Input.vue';
import { computed, ref } from 'vue';
import { getLocalizedField } from '@/utils/get-localized-field.ts';
import type { VehicleType } from '@/entities/vehicle-type.entity.ts';
import { addDays, set } from 'date-fns';
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import { useCalculatePricingModifiersExample } from '@/queries/use-pricing-modifiers.ts';
import type { CreatePricingModifiersDto, ExampleCalculationDto } from '@carvia/ros-client-types';
import { formatCurrency, formatFloat } from '@/utils/format-numbers.ts';

const { pricingModifiers } = defineProps<{
  pricingModifiers: CreatePricingModifiersDto;
}>();

defineEmits(['close']);

const { t, locale } = useI18n();

const vehicleType = ref<VehicleType | null>(null);
const stationId = ref<string | null>(null);
const stationLoad = ref<number | null>(null);
const startDate = ref(
  addDays(set(new Date(), { hours: 11, minutes: 0, seconds: 0, milliseconds: 0 }), 1),
);
const endDate = ref(addDays(startDate.value, 2));
const calculationResult = ref<ExampleCalculationDto>();

const isFilled = computed(() => vehicleType.value && stationId.value);

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);
const stationOptions = computed(
  () =>
    stations.value?.map((station) => ({
      label: getLocalizedField(station.info, locale.value),
      value: station.id,
    })) ?? [],
);

const { mutateAsync: calculateExample, isPending } = useCalculatePricingModifiersExample();

const calculate = async () => {
  if (!vehicleType.value || !stationId.value) {
    return;
  }
  calculationResult.value = await calculateExample({
    vehicleTypeId: vehicleType.value.id,
    stationId: stationId.value,
    stationLoad: typeof stationLoad.value === 'number' ? stationLoad.value / 100 : null,
    startDate: startDate.value,
    endDate: endDate.value,
    modifiers: pricingModifiers.modifiers,
  });
};

const getTemplateName = (id: number) => {
  const template = pricingModifiers.templates.find((template) => template.id === id);
  return template?.name ?? '';
};
</script>

<i18n lang="json">
{
  "en": {
    "vehicleType": "Vehicle type",
    "station": "Station",
    "stationLoad": "Station load",
    "startDate": "Start date",
    "endDate": "End date",
    "calculateExample": "Calculate example",
    "basePricePerDay": "Base price per day",
    "appliedModifiersPerDay": "Applied modifiers per day",
    "day": "Day {count}",
    "dayPrice": "Day price",
    "totalPrice": "Total price",
    "actualStationLoad": "Actual station load"
  },
  "de": {
    "vehicleType": "Vehicle Type",
    "station": "Station",
    "stationLoad": "Stationsauslastung",
    "startDate": "Startdatum",
    "endDate": "Enddatum",
    "calculateExample": "Beispiel berechnen",
    "basePricePerDay": "Basispreis pro Tag",
    "appliedModifiersPerDay": "Angewandte Modifikatoren pro Tag",
    "day": "Tag {count}",
    "dayPrice": "Tagespreis",
    "totalPrice": "Gesamtpreis",
    "actualStationLoad": "Tatsächliche Stationsauslastung"
  },
  "es": {
    "vehicleType": "Tipo de vehículo",
    "station": "Estación",
    "stationLoad": "Ocupación de la estación",
    "startDate": "Fecha de inicio",
    "endDate": "Fecha de fin",
    "calculateExample": "Calcular ejemplo",
    "basePricePerDay": "Precio base por día",
    "appliedModifiersPerDay": "Modificadores aplicados por día",
    "day": "Día {count}",
    "dayPrice": "Precio del día",
    "totalPrice": "Precio total",
    "actualStationLoad": "Ocupación real de la estación"
  }
}
</i18n>
